import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Checkbox } from '../components/ui/checkbox.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Plus, Send, Eye, ClipboardList, Save, Briefcase, Trash2, AlertCircle, FileEdit, UserPlus, Building2, Layers, Stethoscope, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import CargoAutocomplete from '../components/CargoAutocomplete.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu.jsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MODULE_LABELS = { hr: 'Indicadores RH', esg: 'ESG & Pessoas', salary: 'Remuneração', benefits: 'Benefícios' };
const ALL_MODULES = ['hr', 'esg', 'salary', 'benefits'];
const CURRENT_YEAR = new Date().getFullYear();
const HEADCOUNT_DIMENSIONS = [
  { id: 'setor', label: 'Setor (área de negócio)', hint: 'Ex: Industrial, Comercial, TI (pode usar "/" para hierarquia)' },
  { id: 'cargo', label: 'Cargo', hint: 'Autocomplete do catálogo' },
  { id: 'tipo_servico', label: 'Tipo de Serviço', hint: 'Ex: CLT, PJ, Terceirizado' },
];

const Pesquisas = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newSurvey, setNewSurvey] = useState({ titulo: '', descricao: '', modulos: [...ALL_MODULES], periodo: CURRENT_YEAR, cargos: [{ nome: '', descricao_sumaria: '' }], campos_remuneracao: [], headcount_dimensions: [] });
  const [companies, setCompanies] = useState([]);
  const [inviteModal, setInviteModal] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [detailModal, setDetailModal] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [cargosModal, setCargosModal] = useState(null);
  const [editCargos, setEditCargos] = useState([]);
  const [notifyOnSave, setNotifyOnSave] = useState(false);
  const [selectedUpdateCargos, setSelectedUpdateCargos] = useState([]);
  const [inviteTab, setInviteTab] = useState('select'); // 'select' | 'register'
  const [quickForm, setQuickForm] = useState({ razao_social: '', cnpj: '', email: '', telefone: '', nome_respondente: '' });
  const [quickLoading, setQuickLoading] = useState(false);
  const userRole = localStorage.getItem('userRole') || 'respondent';

  const loadSurveys = async () => {
    try {
      const { data } = await axios.get(`${API}/surveys`, { headers: getHeaders() });
      setSurveys(data.surveys || []);
    } catch (e) { console.error(e); }
  };

  const loadCompanies = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/list`, { headers: getHeaders() });
      setCompanies(data.companies || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadSurveys(); loadCompanies(); }, []);

  const handleCreate = async () => {
    if (!newSurvey.titulo.trim()) { toast.error('Informe o título'); return; }
    if (newSurvey.modulos.length === 0) { toast.error('Selecione ao menos um módulo'); return; }
    if (newSurvey.modulos.includes('salary')) {
      const validCargos = newSurvey.cargos.filter(c => c.nome.trim());
      if (validCargos.length === 0) { toast.error('Adicione ao menos um cargo para a pesquisa salarial'); return; }
    }
    try {
      const payload = { ...newSurvey };
      if (payload.modulos.includes('salary')) {
        payload.cargos = newSurvey.cargos.filter(c => c.nome.trim());
      } else {
        delete payload.cargos;
      }
      await axios.post(`${API}/surveys`, payload, { headers: getHeaders() });
      toast.success('Pesquisa criada!');
      setShowCreate(false);
      setNewSurvey({ titulo: '', descricao: '', modulos: [...ALL_MODULES], periodo: CURRENT_YEAR, cargos: [{ nome: '', descricao_sumaria: '' }], campos_remuneracao: [], headcount_dimensions: [] });
      loadSurveys();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const toggleModule = (mod) => {
    setNewSurvey(prev => ({
      ...prev,
      modulos: prev.modulos.includes(mod) ? prev.modulos.filter(m => m !== mod) : [...prev.modulos, mod]
    }));
  };

  const [cargosCatalogo, setCargosCatalogo] = useState([]);

  // Cargo management for creation
  const addCargoRow = () => setNewSurvey(prev => ({ ...prev, cargos: [...prev.cargos, { nome: '', descricao_sumaria: '' }] }));
  const updateCargoRow = (idx, field, value) => setNewSurvey(prev => ({
    ...prev,
    cargos: prev.cargos.map((c, i) => i === idx ? { ...c, [field]: value } : c)
  }));
  const selectCatalogCargo = (idx, cargo) => setNewSurvey(prev => ({
    ...prev,
    cargos: prev.cargos.map((c, i) => i === idx ? {
      ...c,
      nome: cargo.nome,
      descricao_sumaria: c.descricao_sumaria || cargo.descricao_sumaria || '',
    } : c)
  }));
  const removeCargoRow = (idx) => setNewSurvey(prev => ({ ...prev, cargos: prev.cargos.filter((_, i) => i !== idx) }));

  // Helper: nomes já adicionados (para esconder do autocomplete)
  const usedCargoNames = (excludeIdx = -1) =>
    newSurvey.cargos.map((c, i) => i === excludeIdx ? '' : (c.nome || '').trim()).filter(Boolean);

  // Inclui TODOS os cargos do catálogo (que ainda não estão na pesquisa)
  const addAllCatalogCargos = () => {
    if (cargosCatalogo.length === 0) {
      toast.error('Catálogo vazio. Cadastre cargos em Cargos Padrão primeiro.');
      return;
    }
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const existing = new Set(newSurvey.cargos.map(c => norm(c.nome)).filter(Boolean));
    const novos = cargosCatalogo
      .filter(c => !existing.has(norm(c.nome)))
      .map(c => ({ nome: c.nome, descricao_sumaria: c.descricao_sumaria || '' }));
    if (novos.length === 0) {
      toast.info('Todos os cargos do catálogo já estão incluídos');
      return;
    }
    // remove linhas vazias e adiciona os novos
    setNewSurvey(prev => ({
      ...prev,
      cargos: [
        ...prev.cargos.filter(c => (c.nome || '').trim()),
        ...novos,
      ],
    }));
    toast.success(`${novos.length} cargos do catálogo incluídos`);
  };

  // Invite modal
  const openInviteModal = (survey) => {
    setInviteModal(survey);
    setSelectedCompanies([]);
    setInviteTab('select');
    setQuickForm({ razao_social: '', cnpj: '', email: '', telefone: '', nome_respondente: '' });
  };
  const handleInvite = async () => {
    if (selectedCompanies.length === 0) { toast.error('Selecione empresas'); return; }
    try {
      const { data } = await axios.post(`${API}/surveys/${inviteModal.id}/invite`, { company_ids: selectedCompanies }, { headers: getHeaders() });
      toast.success(data.message);
      setInviteModal(null);
      loadSurveys();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  // CNPJ mask: 00.000.000/0000-00
  const maskCnpj = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 14);
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };
  const maskPhone = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d)/, '($1) $2-$3').replace(/(\d{2})(\d{0,4})/, '($1) $2').trim();
    return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim();
  };

  const handleQuickRegister = async (e) => {
    e?.preventDefault?.();
    const f = quickForm;
    if (!f.razao_social.trim() || !f.cnpj.trim() || !f.email.trim()) {
      toast.error('Preencha razão social, CNPJ e email');
      return;
    }
    setQuickLoading(true);
    try {
      const { data } = await axios.post(`${API}/companies/quick-register`, f, { headers: getHeaders() });
      if (data.already_existed) {
        toast.info('Empresa já existia — selecionada para o convite');
      } else {
        toast.success('Empresa cadastrada e selecionada');
      }
      await loadCompanies();
      setSelectedCompanies(prev => prev.includes(data.company_id) ? prev : [...prev, data.company_id]);
      setQuickForm({ razao_social: '', cnpj: '', email: '', telefone: '', nome_respondente: '' });
      setInviteTab('select');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar empresa');
    } finally {
      setQuickLoading(false);
    }
  };

  // Detail modal
  const openDetail = async (survey) => {
    setDetailModal(survey);
    try {
      const { data } = await axios.get(`${API}/surveys/${survey.id}/invitations`, { headers: getHeaders() });
      setInvitations(data.invitations || []);
    } catch (e) { console.error(e); }
  };

  const toggleCompany = (id) => setSelectedCompanies(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const selectAll = () => {
    const respondents = companies.filter(c => c.role === 'respondent').map(c => c.id);
    setSelectedCompanies(prev => prev.length === respondents.length ? [] : respondents);
  };

  // Cargos management modal
  const openCargosModal = (survey) => {
    setCargosModal(survey);
    setEditCargos((survey.cargos || []).map(c => ({ ...c })));
    setNotifyOnSave(false);
    setSelectedUpdateCargos([]);
  };

  const addEditCargoRow = () => setEditCargos(prev => [...prev, { nome: '', descricao_sumaria: '' }]);
  const updateEditCargoRow = (idx, field, value) => setEditCargos(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  const removeEditCargoRow = (idx) => setEditCargos(prev => prev.filter((_, i) => i !== idx));
  const usedEditCargoNames = (excludeIdx = -1) =>
    editCargos.map((c, i) => i === excludeIdx ? '' : (c.nome || '').trim()).filter(Boolean);
  const addAllToEditCargos = () => {
    if (cargosCatalogo.length === 0) { toast.error('Catálogo vazio'); return; }
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const existing = new Set(editCargos.map(c => norm(c.nome)).filter(Boolean));
    const novos = cargosCatalogo.filter(c => !existing.has(norm(c.nome))).map(c => ({ nome: c.nome, descricao_sumaria: c.descricao_sumaria || '' }));
    if (novos.length === 0) { toast.info('Todos os cargos do catálogo já estão incluídos'); return; }
    setEditCargos(prev => [...prev.filter(c => (c.nome || '').trim()), ...novos]);
    toast.success(`${novos.length} cargos do catálogo incluídos`);
  };

  const toggleUpdateCargo = (nome) => setSelectedUpdateCargos(prev => prev.includes(nome) ? prev.filter(n => n !== nome) : [...prev, nome]);

  const handleSaveCargos = async () => {
    const valid = editCargos.filter(c => c.nome.trim());
    if (valid.length === 0) { toast.error('Adicione ao menos um cargo'); return; }
    try {
      await axios.put(`${API}/surveys/${cargosModal.id}/cargos`, {
        cargos: valid,
        notify_respondents: notifyOnSave && selectedUpdateCargos.length > 0,
        cargos_atualizados: selectedUpdateCargos,
      }, { headers: getHeaders() });
      toast.success('Cargos atualizados!');
      setCargosModal(null);
      loadSurveys();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const canManage = userRole === 'admin' || userRole === 'manager';
  const userEmail = (typeof window !== 'undefined' ? localStorage.getItem('userEmail') : '') || '';
  const isG2Admin = userEmail.toLowerCase() === 'g2consultoriarh@gmail.com';

  const handleDeleteSurvey = async (survey) => {
    if (!window.confirm(`Excluir a pesquisa "${survey.titulo}" e TODAS as respostas associadas?\n\nEsta ação é IRREVERSÍVEL.`)) return;
    try {
      const { data } = await axios.delete(`${API}/surveys/${survey.id}`, { headers: getHeaders() });
      toast.success(`Pesquisa excluída. ${JSON.stringify(data.removed)}`);
      loadSurveys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir pesquisa');
    }
  };

  return (
    <MainLayout title="Pesquisas" userRole={userRole}>
      <div className="space-y-6" data-testid="pesquisas-page">
        {canManage && (
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{surveys.length} pesquisa(s)</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="create-survey-btn" className="gap-2">
                  <Plus className="w-4 h-4" /> Nova Pesquisa <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowCreate(true)} data-testid="new-benchmark-survey">
                  <ClipboardList className="w-4 h-4 mr-2" /> Pesquisa de Benchmark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/diagnosticos?novo=1')} data-testid="new-diagnostic-survey">
                  <Stethoscope className="w-4 h-4 mr-2" /> Diagnóstico
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {!canManage && surveys.length === 0 && (
          <div className="text-center py-20 text-slate-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma pesquisa disponível para você no momento.</p>
          </div>
        )}

        <div className="space-y-4">
          {surveys.map(survey => (
            <Card key={survey.id} className="hover:shadow-md transition-shadow" data-testid={`survey-card-${survey.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{survey.titulo}</h3>
                    {survey.descricao && <p className="text-sm text-slate-500 mt-0.5">{survey.descricao}</p>}
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      {(survey.modulos || []).map(m => (
                        <span key={m} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{MODULE_LABELS[m] || m}</span>
                      ))}
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Período: {survey.periodo}</span>
                      {survey.cargos && survey.cargos.length > 0 && (
                        <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{survey.cargos.length} cargo(s)</span>
                      )}
                    </div>
                    {survey.has_pending_cargo_updates && (
                      <div className="flex items-center gap-1.5 mt-2 text-amber-600 text-xs font-medium" data-testid="cargo-update-alert">
                        <AlertCircle className="w-3.5 h-3.5" /> Cargos atualizados pelo gerenciador — atualize seus dados salariais
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${survey.status === 'ativa' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {survey.status}
                    </span>
                    <div className="text-xs text-slate-400">
                      {survey.total_responded}/{survey.total_invited} respostas
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 flex-wrap">
                  {canManage && (
                    <>
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openInviteModal(survey)} data-testid={`invite-btn-${survey.id}`}>
                        <Send className="w-3.5 h-3.5" /> Convidar
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openDetail(survey)} data-testid={`detail-btn-${survey.id}`}>
                        <Eye className="w-3.5 h-3.5" /> Acompanhar
                      </Button>
                      {survey.modulos?.includes('salary') && (
                        <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => openCargosModal(survey)} data-testid={`cargos-btn-${survey.id}`}>
                          <Briefcase className="w-3.5 h-3.5" /> Gerenciar Cargos
                        </Button>
                      )}
                      {/* Manager can also respond to their own survey */}
                      {survey.invite_status !== 'responded' && (
                        <Button size="sm" className="gap-1.5 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = `/responder-pesquisa/${survey.id}`} data-testid={`respond-btn-${survey.id}`}>
                          <FileEdit className="w-3.5 h-3.5" /> Responder (Minha Empresa)
                        </Button>
                      )}
                      {survey.invite_status === 'responded' && (
                        <span className="text-xs text-emerald-600 font-medium px-3 py-1.5 bg-emerald-50 rounded flex items-center gap-1">
                          <FileEdit className="w-3 h-3" /> Minha resposta enviada
                        </span>
                      )}
                      {isG2Admin && (
                        <Button
                          variant="outline" size="sm"
                          className="gap-1.5 text-xs border-red-200 text-red-600 hover:bg-red-50 ml-auto"
                          onClick={() => handleDeleteSurvey(survey)}
                          data-testid={`delete-survey-btn-${survey.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Excluir pesquisa
                        </Button>
                      )}
                    </>
                  )}
                  {!canManage && survey.invite_status === 'pending' && (
                    <Button size="sm" className="gap-1.5 text-xs" onClick={() => window.location.href = `/responder-pesquisa/${survey.id}`} data-testid={`respond-btn-${survey.id}`}>
                      <ClipboardList className="w-3.5 h-3.5" /> Responder Pesquisa
                    </Button>
                  )}
                  {!canManage && survey.invite_status === 'responded' && (
                    <span className="text-xs text-emerald-600 font-medium px-3 py-1.5 bg-emerald-50 rounded">Pesquisa respondida</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CREATE MODAL */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nova Pesquisa</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Título *</Label><Input value={newSurvey.titulo} onChange={e => setNewSurvey(p => ({ ...p, titulo: e.target.value }))} placeholder="Ex: Pesquisa Salarial 2024" data-testid="survey-title-input" /></div>
              <div><Label>Descrição</Label><Input value={newSurvey.descricao} onChange={e => setNewSurvey(p => ({ ...p, descricao: e.target.value }))} placeholder="Descrição opcional" /></div>
              <div><Label>Período</Label><Input type="number" value={newSurvey.periodo} onChange={e => setNewSurvey(p => ({ ...p, periodo: parseInt(e.target.value) || CURRENT_YEAR }))} /></div>
              <div>
                <Label>Módulos *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {ALL_MODULES.map(mod => (
                    <label key={mod} className="flex items-center gap-2 p-2 rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={newSurvey.modulos.includes(mod)} onCheckedChange={() => toggleModule(mod)} />
                      <span className="text-sm">{MODULE_LABELS[mod]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Headcount detail dimensions */}
              <div className="border border-violet-200 rounded-lg p-4 bg-violet-50/30" data-testid="headcount-dim-section">
                <Label className="text-sm font-semibold">Detalhamento de Headcount <span className="text-slate-400 font-normal">(opcional)</span></Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-3">
                  Quando marcado, o respondente preencherá uma tabela com uma linha por valor de cada dimensão.
                  Use isto para análises por setor, cargo ou tipo de serviço.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {HEADCOUNT_DIMENSIONS.map(d => {
                    const checked = newSurvey.headcount_dimensions.includes(d.id);
                    return (
                      <label key={d.id} className={`flex items-start gap-2 p-2.5 rounded border cursor-pointer transition-colors ${checked ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => setNewSurvey(p => ({
                            ...p,
                            headcount_dimensions: checked
                              ? p.headcount_dimensions.filter(x => x !== d.id)
                              : [...p.headcount_dimensions, d.id]
                          }))}
                          data-testid={`headcount-dim-${d.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{d.label}</p>
                          <p className="text-[10px] text-slate-500">{d.hint}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {newSurvey.headcount_dimensions.length > 0 && (
                  <p className="text-[11px] text-violet-700 mt-2" data-testid="headcount-dim-summary">
                    Será incluído o módulo "Headcount Detalhado" com as dimensões: <strong>{newSurvey.headcount_dimensions.map(d => HEADCOUNT_DIMENSIONS.find(x => x.id === d).label).join(', ')}</strong>.
                  </p>
                )}
              </div>

              {/* Cargo definition section - only when salary is selected */}
              {newSurvey.modulos.includes('salary') && (
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30" data-testid="cargo-definition-section">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-sm font-semibold">Cargos da Pesquisa Salarial *</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Defina os cargos que serão pesquisados com nomenclatura e descrição sumária</p>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addAllCatalogCargos}
                        className="text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid="add-all-cargos-btn"
                        title={cargosCatalogo.length > 0 ? `${cargosCatalogo.length} cargos disponíveis no catálogo` : 'Catálogo vazio'}
                      >
                        <Layers className="w-3 h-3" /> Incluir todos do catálogo
                        {cargosCatalogo.length > 0 && <span className="ml-1 text-[10px] opacity-70">({cargosCatalogo.length})</span>}
                      </Button>
                      <Button variant="outline" size="sm" onClick={addCargoRow} className="text-xs gap-1" data-testid="add-cargo-btn">
                        <Plus className="w-3 h-3" /> Cargo
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {newSurvey.cargos.map((cargo, idx) => (
                      <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`cargo-row-${idx}`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <Label className="text-[11px] text-slate-500">Nomenclatura do Cargo *</Label>
                            <CargoAutocomplete
                              value={cargo.nome}
                              onChange={v => updateCargoRow(idx, 'nome', v)}
                              onSelect={c => selectCatalogCargo(idx, c)}
                              testId={`cargo-nome-${idx}`}
                              excludedNames={usedCargoNames(idx)}
                              onCatalogReady={idx === 0 ? setCargosCatalogo : undefined}
                            />
                          </div>
                          <button onClick={() => removeCargoRow(idx)} className="text-red-400 hover:text-red-600 mt-5" data-testid={`remove-cargo-${idx}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Descrição Sumária do Cargo</Label>
                          <Textarea value={cargo.descricao_sumaria} onChange={e => updateCargoRow(idx, 'descricao_sumaria', e.target.value)} placeholder="Breve descrição das responsabilidades..." className="text-sm min-h-[50px]" data-testid={`cargo-desc-${idx}`} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Custom remuneration fields section */}
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label className="text-sm font-semibold">Componentes de Remuneração Adicionais</Label>
                        <p className="text-xs text-slate-500 mt-0.5">Além de salário base, HE, adic. noturno, periculosidade e insalubridade (já incluídos), defina campos livres</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setNewSurvey(prev => ({ ...prev, campos_remuneracao: [...prev.campos_remuneracao, ''] }))} className="text-xs gap-1" data-testid="add-remun-field-btn">
                        <Plus className="w-3 h-3" /> Campo
                      </Button>
                    </div>
                    {newSurvey.campos_remuneracao.length === 0 && (
                      <p className="text-xs text-slate-400 py-2">Nenhum campo adicional. Clique em "+ Campo" para adicionar (ex: Comissão, Gratificação, Bônus).</p>
                    )}
                    <div className="space-y-2">
                      {newSurvey.campos_remuneracao.map((campo, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <Input
                            value={campo}
                            onChange={e => setNewSurvey(prev => ({ ...prev, campos_remuneracao: prev.campos_remuneracao.map((c, i) => i === ci ? e.target.value : c) }))}
                            placeholder="Ex: Comissão, Gratificação, Bônus..."
                            className="h-8 text-sm"
                            data-testid={`remun-field-${ci}`}
                          />
                          <button onClick={() => setNewSurvey(prev => ({ ...prev, campos_remuneracao: prev.campos_remuneracao.filter((_, i) => i !== ci) }))} className="text-red-400 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <Button onClick={handleCreate} className="w-full" data-testid="confirm-create-survey-btn">Criar Pesquisa</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* INVITE MODAL */}
        <Dialog open={!!inviteModal} onOpenChange={() => setInviteModal(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Convidar Empresas — {inviteModal?.titulo}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Selecione empresas já cadastradas ou cadastre uma nova respondente.</DialogDescription>
            </DialogHeader>

            <div className="flex bg-slate-100 rounded-lg p-0.5 mb-3">
              <button
                onClick={() => setInviteTab('select')}
                data-testid="invite-tab-select"
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inviteTab === 'select' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Building2 className="w-3.5 h-3.5" />
                Selecionar Existentes ({companies.filter(c => c.role === 'respondent').length})
              </button>
              <button
                onClick={() => setInviteTab('register')}
                data-testid="invite-tab-register"
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inviteTab === 'register' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                Cadastrar Nova
              </button>
            </div>

            {inviteTab === 'select' ? (
              <div className="space-y-3">
                <Button variant="outline" size="sm" onClick={selectAll} className="text-xs">{selectedCompanies.length === companies.filter(c => c.role === 'respondent').length ? 'Desmarcar Todas' : 'Selecionar Todas Respondentes'}</Button>
                <div className="space-y-1.5 max-h-[45vh] overflow-y-auto">
                  {companies.filter(c => c.role === 'respondent').map(c => (
                    <label key={c.id} className="flex items-center gap-2 p-2 rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={selectedCompanies.includes(c.id)} onCheckedChange={() => toggleCompany(c.id)} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-900">{c.razao_social}</span>
                        <span className="text-xs text-slate-400 ml-2">{c.segmento}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <Button onClick={handleInvite} className="w-full gap-2" data-testid="confirm-invite-btn">
                  <Send className="w-4 h-4" /> Enviar {selectedCompanies.length} Convite(s)
                </Button>
              </div>
            ) : (
              <form onSubmit={handleQuickRegister} className="space-y-3" data-testid="quick-register-form">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-[11px] text-blue-700 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Cadastro rápido para convidar uma nova empresa respondente. A própria empresa poderá complementar segmento, porte e demais dados ao acessar a plataforma.</span>
                </div>
                <div>
                  <Label className="text-xs">Razão Social *</Label>
                  <Input
                    value={quickForm.razao_social}
                    onChange={e => setQuickForm(p => ({ ...p, razao_social: e.target.value }))}
                    placeholder="Empresa LTDA"
                    data-testid="quick-razao-social"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">CNPJ *</Label>
                    <Input
                      value={quickForm.cnpj}
                      onChange={e => setQuickForm(p => ({ ...p, cnpj: maskCnpj(e.target.value) }))}
                      placeholder="00.000.000/0000-00"
                      data-testid="quick-cnpj"
                      maxLength={18}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Telefone</Label>
                    <Input
                      value={quickForm.telefone}
                      onChange={e => setQuickForm(p => ({ ...p, telefone: maskPhone(e.target.value) }))}
                      placeholder="(11) 99999-9999"
                      data-testid="quick-telefone"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Email do Respondente *</Label>
                    <Input
                      type="email"
                      value={quickForm.email}
                      onChange={e => setQuickForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="rh@empresa.com.br"
                      data-testid="quick-email"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nome do Respondente</Label>
                    <Input
                      value={quickForm.nome_respondente}
                      onChange={e => setQuickForm(p => ({ ...p, nome_respondente: e.target.value }))}
                      placeholder="Maria Silva"
                      data-testid="quick-nome"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={quickLoading} className="w-full gap-2" data-testid="quick-register-submit">
                  <UserPlus className="w-4 h-4" />
                  {quickLoading ? 'Cadastrando...' : 'Cadastrar e Adicionar ao Convite'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* DETAIL/TRACK MODAL */}
        <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Acompanhamento — {detailModal?.titulo}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <div className="bg-blue-50 rounded-lg p-3 flex-1 text-center">
                  <p className="text-2xl font-bold text-blue-600">{detailModal?.total_invited || 0}</p>
                  <p className="text-xs text-blue-500">Convidadas</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 flex-1 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{detailModal?.total_responded || 0}</p>
                  <p className="text-xs text-emerald-500">Responderam</p>
                </div>
              </div>
              <div className="space-y-1.5">
                {invitations.map((inv, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-900">{inv.company_name}</p>
                      <p className="text-xs text-slate-400">{inv.company_email}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${inv.status === 'responded' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {inv.status === 'responded' ? 'Respondida' : 'Pendente'}
                    </span>
                  </div>
                ))}
                {invitations.length === 0 && <p className="text-center text-sm text-slate-400 py-4">Nenhum convite enviado</p>}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* CARGOS MANAGEMENT MODAL */}
        <Dialog open={!!cargosModal} onOpenChange={() => setCargosModal(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Gerenciar Cargos — {cargosModal?.titulo}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Edite, adicione ou remova cargos. Marque os que foram atualizados para notificar os respondentes.
              </div>
              <div className="space-y-3">
                {editCargos.map((cargo, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`edit-cargo-row-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Label className="text-[11px] text-slate-500">Nomenclatura do Cargo</Label>
                        <CargoAutocomplete
                          value={cargo.nome}
                          onChange={v => updateEditCargoRow(idx, 'nome', v)}
                          onSelect={c => {
                            updateEditCargoRow(idx, 'nome', c.nome);
                            if (!cargo.descricao_sumaria && c.descricao_sumaria) {
                              updateEditCargoRow(idx, 'descricao_sumaria', c.descricao_sumaria);
                            }
                          }}
                          testId={`edit-cargo-nome-${idx}`}
                          excludedNames={usedEditCargoNames(idx)}
                          onCatalogReady={idx === 0 ? setCargosCatalogo : undefined}
                        />
                      </div>
                      <button onClick={() => removeEditCargoRow(idx)} className="text-red-400 hover:text-red-600 mt-5">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Descrição Sumária</Label>
                      <Textarea value={cargo.descricao_sumaria} onChange={e => updateEditCargoRow(idx, 'descricao_sumaria', e.target.value)} placeholder="Descrição do cargo..." className="text-sm min-h-[50px]" data-testid={`edit-cargo-desc-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={addAllToEditCargos} className="text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="add-all-edit-cargos-btn">
                  <Layers className="w-3 h-3" /> Incluir todos do catálogo {cargosCatalogo.length > 0 && <span className="opacity-70">({cargosCatalogo.length})</span>}
                </Button>
                <Button variant="outline" size="sm" onClick={addEditCargoRow} className="text-xs gap-1" data-testid="add-edit-cargo-btn">
                  <Plus className="w-3 h-3" /> Adicionar Cargo
                </Button>
              </div>

              {/* Notify section */}
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={notifyOnSave} onCheckedChange={setNotifyOnSave} data-testid="notify-checkbox" />
                  <span className="text-sm text-slate-700">Notificar respondentes sobre cargos atualizados</span>
                </label>
                {notifyOnSave && editCargos.filter(c => c.nome.trim()).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-amber-800">Selecione os cargos que foram atualizados:</p>
                    <div className="space-y-1">
                      {editCargos.filter(c => c.nome.trim()).map((c, i) => (
                        <label key={i} className="flex items-center gap-2 p-1.5 cursor-pointer hover:bg-amber-100/50 rounded text-sm">
                          <Checkbox checked={selectedUpdateCargos.includes(c.nome)} onCheckedChange={() => toggleUpdateCargo(c.nome)} />
                          <span>{c.nome}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={handleSaveCargos} className="w-full gap-2" data-testid="save-cargos-btn">
                <Save className="w-4 h-4" /> Salvar Cargos
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Pesquisas;
